import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogTitle } from '../../material-ui/Dialog';
import { List, ListItem, ListItemIcon, ListItemText } from '../../material-ui/List';
import { TextField } from '../../material-ui/TextField';
import { Button } from '../../material-ui/Button';
import { Checkbox } from '../../material-ui/Checkbox';
import { useStyles } from '../useStyles';

/*
 * Личные чаты (1-на-1) ВРЕМЕННО отключены по требованию (Рощин): выборка по таблицам
 * Личных дел и пользователей (Pr_PersonnelID/Au_UserID) создаёт риск нагрузки на БД IS.
 * Функционал не удалён, а скрыт флагом — чтобы вернуть, выставить PERSONAL_CHATS_ENABLED = true
 * здесь и в backend (data-providers/messenger.js, routes/messenger.js).
 */
const PERSONAL_CHATS_ENABLED = false;

/*
 * Диалог создания нового чата.
 *  - «Личный диалог»: выбор одного адресата (клик → старт переписки) — под флагом.
 *  - «Группа»: шаг 1 — название; шаг 2 — выбор участников → создание.
 * Изолированный компонент: получает список адресатов и колбэки, Redux не знает.
 */
export const ComposeDialog = memo(({ open, recipients, onSelect, onCreateGroup, onClose }) => {
    const classes = useStyles();
    const { t: translate } = useTranslation();
    // При отключённых личных чатах диалог всегда работает в режиме группы.
    const [mode, setMode] = useState(PERSONAL_CHATS_ENABLED ? 'personal' : 'group');
    const [groupStep, setGroupStep] = useState('name');
    const [search, setSearch] = useState('');
    const [groupName, setGroupName] = useState('');
    const [selected, setSelected] = useState([]);

    // Сбрасываем состояние при закрытии, чтобы при следующем открытии было чисто.
    useEffect(() => {
        if (!open) {
            setMode(PERSONAL_CHATS_ENABLED ? 'personal' : 'group');
            setGroupStep('name');
            setSearch('');
            setGroupName('');
            setSelected([]);
        }
    }, [open]);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return recipients;
        }

        return recipients.filter((recipient) => (recipient.name || '').toLowerCase().includes(query));
    }, [recipients, search]);

    const isGroup = mode === 'group';
    const isGroupNameStep = isGroup && groupStep === 'name';
    const isGroupMembersStep = isGroup && groupStep === 'members';
    const trimmedGroup = groupName.trim();

    const toggle = useCallback((id) => {
        setSelected((prev) => (prev.indexOf(id) === -1 ? [...prev, id] : prev.filter((value) => value !== id)));
    }, []);

    const submitGroup = useCallback(() => {
        if (trimmedGroup) {
            onCreateGroup(trimmedGroup, selected);
        }
    }, [trimmedGroup, selected, onCreateGroup]);

    const handleItemClick = useCallback(
        (recipient) => {
            if (isGroupMembersStep) {
                toggle(recipient.id);
            } else if (!isGroup) {
                onSelect(recipient);
            }
        },
        [isGroup, isGroupMembersStep, toggle, onSelect]
    );

    const handleModePersonal = useCallback(() => {
        setMode('personal');
        setGroupStep('name');
        setSearch('');
        setSelected([]);
    }, []);

    const handleModeGroup = useCallback(() => {
        setMode('group');
        setGroupStep('name');
        setSearch('');
        setSelected([]);
    }, []);

    const dialogTitle = isGroupNameStep
        ? translate('messenger.newChat')
        : isGroupMembersStep
        ? translate('messenger.groupMembersStep')
        : translate('messenger.newChat');

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent className={classes.composeContent}>
                {/* Переключатель «Личный/Группа» скрыт: личные чаты временно отключены (Рощин). */}
                {PERSONAL_CHATS_ENABLED && (
                    <div className={classes.composeModeRow}>
                        <Button size="small" variant={isGroup ? 'outlined' : 'contained'} color="primary" onClick={handleModePersonal}>
                            {translate('messenger.newPersonal')}
                        </Button>
                        <Button size="small" variant={isGroup ? 'contained' : 'outlined'} color="primary" onClick={handleModeGroup}>
                            {translate('messenger.newGroup')}
                        </Button>
                    </div>
                )}

                {isGroupNameStep && (
                    <>
                        <TextField
                            autoFocus={true}
                            fullWidth={true}
                            value={groupName}
                            onChange={(event) => setGroupName(event.target.value)}
                            placeholder={translate('messenger.groupNamePlaceholder')}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && trimmedGroup) {
                                    event.preventDefault();
                                    setGroupStep('members');
                                }
                            }}
                        />
                        <div className={classes.composeGroupActions}>
                            <Button color="primary" variant="contained" disabled={!trimmedGroup} onClick={() => setGroupStep('members')}>
                                {translate('messenger.nextStep')}
                            </Button>
                        </div>
                    </>
                )}

                {isGroupMembersStep && (
                    <>
                        <div className={classes.composeSubtitle}>{trimmedGroup}</div>
                        <TextField
                            autoFocus={true}
                            fullWidth={true}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={translate('messenger.membersSearch')}
                        />
                        <List className={classes.recipientList}>
                            {filtered && filtered.length > 0 ? (
                                filtered.map((recipient) => (
                                    <ListItem button={true} key={recipient.id} onClick={() => handleItemClick(recipient)}>
                                        <ListItemIcon className={classes.composeCheckCell}>
                                            <Checkbox
                                                edge="start"
                                                color="primary"
                                                tabIndex={-1}
                                                disableRipple={true}
                                                checked={selected.indexOf(recipient.id) !== -1}
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={recipient.name} secondary={recipient.role || undefined} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText primary={translate('messenger.noRecipients')} />
                                </ListItem>
                            )}
                        </List>
                        <div className={classes.composeGroupActions}>
                            <Button onClick={() => setGroupStep('name')}>{translate('messenger.backStep')}</Button>
                            <Button color="primary" variant="contained" disabled={!trimmedGroup} onClick={submitGroup}>
                                {selected.length > 0
                                    ? `${translate('messenger.createGroup')} (${selected.length})`
                                    : translate('messenger.createGroup')}
                            </Button>
                        </div>
                    </>
                )}

                {/* Личный диалог (1-на-1) временно отключён (Рощин): выборка по Личным делам/пользователям. */}
                {PERSONAL_CHATS_ENABLED && !isGroup && (
                    <>
                        <TextField
                            autoFocus={true}
                            fullWidth={true}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={translate('messenger.recipientsSearch')}
                        />
                        <List className={classes.recipientList}>
                            {filtered && filtered.length > 0 ? (
                                filtered.map((recipient) => (
                                    <ListItem button={true} key={recipient.id} onClick={() => handleItemClick(recipient)}>
                                        <ListItemText primary={recipient.name} secondary={recipient.role || undefined} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText primary={translate('messenger.noRecipients')} />
                                </ListItem>
                            )}
                        </List>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
});

ComposeDialog.displayName = 'ComposeDialog';
